/**
 * Price Check strip (2026-09-04, owner-signed design - mockup artifact
 * 596584d0). One pill + one sentence + one cheap-to-expensive bar with a
 * small amber TODAY dot: the two-second answer to "is this actually a
 * good price?", computed at cache-write time from the price diary the
 * platform has kept since May (price_observations).
 *
 * Server component, static markup, no JavaScript: the facts are in the
 * SSR HTML for Google and AI crawlers - that visibility is the SEO point
 * of the feature. Renders NOTHING when data is null or insufficient
 * (old cache rows, flag off, or brand-new products), so pages are
 * byte-identical to today until real history exists.
 *
 * Colors are the comparison surface's own tokens (--cmp-good/--cmp-amber
 * pairs defined for light and dark in globals.css) plus the -soft fills
 * added alongside them; theme switching is automatic.
 */

interface PriceCheckData {
  tier?: "full" | "medium" | "sparse";
  verdict?: "best_yet" | "typical" | "higher_than_usual";
  today?: number;
  lowest?: number;
  highest?: number;
  typical?: number;
  marker_pct?: number;
  since?: string;
  days?: number;
  low_month?: string;
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December",
];

function sinceLabel(since?: string): string {
  if (!since) return "";
  const d = new Date(since + "T00:00:00Z");
  if (Number.isNaN(d.getTime())) return "";
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

function sinceDay(since?: string): string {
  if (!since) return "recently";
  const d = new Date(since + "T00:00:00Z");
  if (Number.isNaN(d.getTime())) return "recently";
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

function dollars(n?: number): string {
  if (n == null || !Number.isFinite(n)) return "";
  return `$${Math.floor(n).toLocaleString("en-AU")}`;
}

function roundTen(n?: number): string {
  if (n == null || !Number.isFinite(n)) return "";
  return `$${(Math.round(n / 10) * 10).toLocaleString("en-AU")}`;
}

export function PriceCheckStrip({ data }: { data?: PriceCheckData | null }) {
  if (!data || !data.tier) return null;

  const since = sinceLabel(data.since);

  // Sparse (owner-signed copy + design 2026-09-05): same pill + sentence
  // anatomy as every other state - soft amber "in progress" pill, short
  // promise of when the price check arrives. No dot, no bar, no date noise.
  if (data.tier === "sparse") {
    if (!since) return null;
    return (
      <section className="mt-4 rounded-2xl bg-secondary px-6 py-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="pc-pill-track whitespace-nowrap rounded-full px-3 py-1 text-[13px] font-bold">
            Tracking started
          </span>
          <span className="text-[14.5px] text-foreground">
            Your price check appears here once we have a few days of prices.
          </span>
        </div>
      </section>
    );
  }

  const verdict = data.verdict ?? "typical";
  const pill =
    verdict === "best_yet"
      ? { text: "Best price yet", cls: "pc-pill-good" }
      : verdict === "higher_than_usual"
        ? { text: "Higher than usual", cls: "pc-pill-warn" }
        : { text: "Typical price", cls: "pc-pill-neutral" };

  const typicalTxt = roundTen(data.typical);
  const full = data.tier === "full";

  // Owner-signed copy (2026-09-04). Plain sentences, no widget
  // self-commentary, and never an em-dash.
  const sentence =
    verdict === "best_yet" ? (
      full ? (
        <>
          The lowest price we&rsquo;ve seen.{" "}
          <span className="text-muted-foreground">
            It usually sells for around {typicalTxt}.
          </span>
        </>
      ) : (
        <>
          The lowest price we&rsquo;ve seen since tracking began
          {data.since ? ` in ${sinceLabel(data.since).split(" ")[0]}` : ""}.
        </>
      )
    ) : verdict === "higher_than_usual" ? (
      full ? (
        <>
          Usually sells for around {typicalTxt}
          {data.low_month && data.lowest != null
            ? `, and it dropped to ${dollars(data.lowest)} in ${data.low_month}`
            : ""}
          .{" "}
          <span className="text-muted-foreground">
            Watch the price and we&rsquo;ll email you when it drops.
          </span>
        </>
      ) : (
        <>
          {data.low_month && data.lowest != null
            ? `It was ${dollars(data.lowest)} in ${data.low_month}. `
            : ""}
          <span className="text-muted-foreground">
            Watch the price and we&rsquo;ll email you when it drops.
          </span>
        </>
      )
    ) : full ? (
      <>
        Usually sells for around {typicalTxt}.{" "}
        <span className="text-muted-foreground">
          The lowest we&rsquo;ve seen is {dollars(data.lowest)}.
        </span>
      </>
    ) : (
      <>
        Usually around {typicalTxt}.{" "}
        <span className="text-muted-foreground">
          The lowest we&rsquo;ve seen is {dollars(data.lowest)}.
        </span>
      </>
    );

  const marker = Math.max(0, Math.min(100, data.marker_pct ?? 0));

  return (
    <section className="mt-4 rounded-2xl bg-secondary px-6 pb-4 pt-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2.5">
        <span
          className={`${pill.cls} whitespace-nowrap rounded-full px-3 py-1 text-[13px] font-bold`}
        >
          {pill.text}
        </span>
        <span className="text-[14.5px] text-foreground">{sentence}</span>
      </div>

      {data.tier === "full" &&
      data.lowest != null &&
      data.highest != null ? (
        <>
          <div className="pc-band relative mx-1 mt-8 h-2 rounded-full">
            <span
              className="pc-dot absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{ left: `${marker}%` }}
              aria-hidden="true"
            >
              <span className="pc-today-label absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 text-[9.5px] font-bold tracking-[0.14em]">
                TODAY
              </span>
            </span>
          </div>
          <div
            className="mx-1 mt-2 flex justify-between text-xs text-muted-foreground"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            <span>
              <b className="font-semibold text-foreground">
                {dollars(data.lowest)}
              </b>{" "}
              lowest tracked
            </span>
            <span>{dollars(data.highest)} highest tracked</span>
          </div>
        </>
      ) : null}

      {since ? (
        <p className="mt-2.5 text-[12.5px] text-muted-foreground">
          Price tracked since {since}
        </p>
      ) : null}
    </section>
  );
}
